<script lang="ts" module>
	import type {
		SbBlogArticleCategoryStory,
		SbBlogArticleStory,
		SbBlogExternalArticleStory
	} from '../types';

	let categoriesPromise = $state<Promise<SbBlogArticleCategoryStory[]>>();
</script>

<script lang="ts">
	import type { ISbStoryData } from '@storyblok/js';
	import PageGrid from '../components/pageGrid.svelte';
	import Magnifier from '$lib/icons/magnifier.svelte';
	import ChevronDown from '$lib/icons/chevronDown.svelte';
	import { slide } from 'svelte/transition';
	import { onMount } from 'svelte';
	import CategoryFilterListEntry from '../parts/categoryFilterListEntry.svelte';
	import { afterNavigate, goto, pushState } from '$app/navigation';
	import { page } from '$app/state';
	import StorySearchInput from '../parts/storySearchInput.svelte';
	import StorySearchResult from '../parts/storySearchResult.svelte';
	import CategoryDropdown from '../parts/categoryDropdown.svelte';
	import DesktopPageGrid from '../components/desktopPageGrid.svelte';
	import Log from '$lib/utils/log';
	import Button from '$lib/buttons/v3/button.svelte';
	import { device } from '$lib/state/device.svelte';
	import { storyblok } from '$lib/state/storyblok.svelte';
	interface Props {
		content: ISbStoryData['content'] & { title: string; default_category_name: string };
		open?: boolean;
	}

	let { content, open = false }: Props = $props();

	async function getCategoriesByType(type: 'news' | 'blog'): Promise<SbBlogArticleCategoryStory[]> {
		try {
			const { data } = await storyblok.client.getStories({
				by_slugs: `${type}/categories/*`,
				resolve_relations: ['blog_page.category']
			});

			return data.stories as SbBlogArticleCategoryStory[];
		} catch (e) {
			Log.exception(e);

			return [];
		}
	}

	async function getCategories(): Promise<SbBlogArticleCategoryStory[]> {
		const [newsCategories, blogCategories] = await Promise.all([
			getCategoriesByType('news'),
			getCategoriesByType('blog')
		]);

		return [...newsCategories, ...blogCategories];
	}

	onMount(() => {
		if (!categoriesPromise) {
			categoriesPromise = getCategories();
		}
	});

	let selectedCategoryName = $derived(
		page.state.selectedBlogArticleCategory
			? page.state.selectedBlogArticleCategory.content.name
			: content.default_category_name
	);

	function selectCategory(category: SbBlogArticleCategoryStory | null) {
		open = false;

		const state = { ...page.state, selectedBlogArticleCategory: category };
		const searchParams = new URLSearchParams(window.location.search);
		if (category) {
			searchParams.set('category', category.slug);
		}

		const currentRoot = page.route.id?.match(/^\/([^\/]*)\/([^\/]*)/)?.[1];

		const root = category?.full_slug
			? `/${category.full_slug.split('/').shift()}`
			: currentRoot
				? `/${currentRoot}`
				: page.route.id || '/news';

		if (page.route.id !== root) {
			goto(`${root}?${searchParams}`, { state });
			return;
		}

		pushState(`${window.location.pathname}?${searchParams}`, state);
	}

	let searchTerm = $state('');
	let results = $state<Array<SbBlogArticleStory | SbBlogExternalArticleStory> | null>(null);
	let searching = $state(false);

	function setResults(searchResults: typeof results) {
		searching = false;
		results = searchResults;

		if (results) {
			open = true;
			return;
		}

		if (device.data.isDesktop) {
			open = false;
		}
	}

	function startSearch() {
		searching = true;
		open = true;
	}

	afterNavigate(() => {
		open = false;
	});
</script>

<div class="bg-white max-lg:px-6">
	<button
		type="button"
		class="relative z-50 col-span-full flex w-full items-center bg-white pb-[14px] pt-[10px] text-brown-1 max-lg:justify-between"
		class:lg:cursor-auto={!results}
		onclick={() => {
			if (!device.data.isDesktop) {
				open = !open;
				return;
			}

			if (results) {
				open = true;
			}
		}}
	>
		<div class="text-[20px]/[140%] font-semibold lg:hidden">
			{content.title}
		</div>
		<div class="flex-1 pl-44 max-lg:hidden">
			<div class="mx-auto w-1/4 min-w-52 2xl:w-1/5">
				<StorySearchInput
					grid={false}
					bind:searchTerm
					onresults={(results) => setResults(results)}
					onsearch={startSearch}
				/>
			</div>
		</div>
		<div class="flex items-center gap-x-2 lg:hidden">
			<Magnifier class="h-6 w-6" />
			<div class="text-[16px]/[140%]">{selectedCategoryName}</div>
			<div class="transform-gpu transition-transform" class:rotate-180={open}>
				<ChevronDown class="h-6 w-6" />
			</div>
		</div>
		<div class="flex items-center justify-end gap-x-8 max-lg:hidden lg:min-w-[304px]">
			{#if categoriesPromise}
				<CategoryDropdown {categoriesPromise} onselect={(category) => selectCategory(category)} />
			{/if}
			<Button class="btn-large max-lg:hidden" href="/food-recycler">Buy now</Button>
		</div>
	</button>
	{#if open}
		<div
			class="fixed inset-0 z-40 mt-24 flex h-full w-full flex-col gap-y-6 bg-white pt-6 lg:bg-transparent"
			transition:slide
		>
			<div class="lg:hidden">
				<PageGrid>
					<StorySearchInput
						bind:searchTerm
						onresults={(results) => setResults(results)}
						onsearch={startSearch}
					/>
				</PageGrid>
			</div>
			<div class="h-full w-full lg:bg-white">
				{#if results || searching}
					<PageGrid class="border-b border-b-stone-4">
						<div
							class="col-span-full pb-6 pt-8 text-[16px]/[140%] text-brown-2 lg:col-start-4 lg:col-end-11 lg:pb-10 lg:pt-28 lg:text-[24px]/[140%]"
						>
							{#if searching}
								<span>Searching...</span>
							{:else if results}
								<span>{results.length} {results.length === 1 ? 'result' : 'results'} found</span>
							{/if}
						</div>
					</PageGrid>
					{#if results && !searching}
						<DesktopPageGrid
							class="h-full"
							gridClass="flex flex-col overflow-y-auto scrollbar-hide h-full pb-64"
						>
							{#each results as result}
								<StorySearchResult story={result} />
							{/each}
						</DesktopPageGrid>
					{/if}
				{:else}
					<PageGrid gridClass="lg:hidden">
						<ul
							class="col-span-full flex flex-col gap-y-2 text-[20px]/[140%] lg:col-start-4 lg:col-end-11"
						>
							{#if page.route.id?.startsWith('/news')}
								<li
									class:text-brown-2={page.state.selectedBlogArticleCategory}
									class:text-green-3={!page.state.selectedBlogArticleCategory}
								>
									<a href="/blog">Latest stories</a>
								</li>
							{:else}
								<li
									class:text-brown-2={page.state.selectedBlogArticleCategory}
									class:text-green-3={!page.state.selectedBlogArticleCategory}
								>
									<a href="/blog">Latest stories</a>
								</li>
							{/if}

							{#await categoriesPromise}
								<li>Loading categories...</li>
							{:then categories}
								{#if categories}
									{#each categories as category}
										<CategoryFilterListEntry
											active={page.state.selectedBlogArticleCategory?.uuid === category.uuid}
											onclick={() => selectCategory(category)}
											>{category.content.name}</CategoryFilterListEntry
										>
									{/each}
								{:else}
									<li>No categories found</li>
								{/if}
							{/await}
						</ul>
					</PageGrid>
				{/if}
			</div>
		</div>
	{/if}
</div>
